<script setup lang="ts">
// TODO refactor that shit
import SectionComponent from '@/vue/molecules/components/section-component.vue';
import {useRouter} from 'vue-router';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useNextLink} from '@/ts/composables/stateful/use-links';
import {useInterventionStore} from '@/ts/store/intervention-store';
import {useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {
  computed,
  nextTick,
  onBeforeMount,
  provide,
  ref,
  watch,
} from 'vue';
import {
  type Component,
  ComponentType,
  ContentDisplayType,
  type Intervention,
  PageState,
  SessionState,
} from '@/ts/types/dto/interventions-dto';
import {usePageId} from '@/ts/composables/stateful/use-page';
import {useSessionId} from '@/ts/composables/stateful/use-session';
import {CURRENT_SESSION_ID} from '@/ts/composables/stateful/use-session-weight';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {QForm} from 'quasar';
import type {SessionRequest} from '@/ts/types/dto/session-request-dto';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import type {ComponentPublicInstance} from '@vue/runtime-core';
import {findScrollContainer, useScrollToElement} from '@/ts/composables/pure/use-scroll-to';
import {useSectionRefs} from '@/ts/composables/pure/use-section-refs';
import {SMALL_PADDING} from '@/ts/utils/css-consts';

const api = useApi();
const logger = useLogger();
const intervention = useInterventionStore();
const {pid, sid, iid} = useRouterSectionParams();
// Expose the current session id to nested weight inputs / the weight-loss
// display so they can pre-fill within this session (and reset across sessions).
provide(CURRENT_SESSION_ID, sid);
const router = useRouter();
const hideContinue = ref(false);
const navigating = ref(false);
const nextPageLink = useNextLink(sid, pid);
const model = ref<SessionRequest[][]>([]);
const currentIndex = ref(1);
const sectionRefs = useSectionRefs();

const scrollToSection = useScrollToElement(SMALL_PADDING);

function onValidationError(elem: any): void {
  const element = (elem as ComponentPublicInstance).$el as HTMLElement;

  const invalidInput = element.querySelector('.q-field--error') ?? element;

  // Find the actual scrollable container by walking up the DOM tree
  let scrollContainer: HTMLElement | null = invalidInput.parentElement;
  while (scrollContainer) {
    const {overflowY} = window.getComputedStyle(scrollContainer);
    if (overflowY === 'scroll' || (overflowY === 'auto' && scrollContainer.scrollHeight > scrollContainer.clientHeight)) {
      break;
    }
    scrollContainer = scrollContainer.parentElement;
  }

  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = invalidInput.getBoundingClientRect();
    const offsetTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;

    scrollContainer.scrollTo({
      top: offsetTop - SMALL_PADDING,
      behavior: 'smooth',
    });
  }

  const input = invalidInput.querySelector('input, textarea, select') as HTMLElement | null;
  if (input) {
    setTimeout(() => {
      input.focus();
    }, 300);
  }
}
const {session} = useSessionId(sid);
// This component only renders for an unlocked session on an in-range page (gated by
// the parent's `v-if`), so the page is guaranteed present here.
const {page: currentPage} = usePageId(sid, pid);
const page = computed(() => currentPage.value!);


const allContentRead = computed(() => {
  return currentIndex.value >= page.value.components.length;
});

function isComponentAnswered(comp: Component, index: number): boolean {
  const hasLocalAnswers = (model.value[index]?.length ?? 0) > 0;
  return hasLocalAnswers
    || Boolean(comp.questionnaire?.isAnswered)
    || Boolean(comp.journal?.isAnswered)
    || Boolean(comp.problemSolvingWorksheet?.isAnswered)
    || Boolean(comp.conceptualizationWorksheet?.isAnswered);
}

const NON_ANSWERABLE_TYPES: ComponentType[] = [
  ComponentType.CONTENT,
  ComponentType.TABLE,
  ComponentType.QUESTIONNAIRE_REVIEW,
  ComponentType.IMAGE_GRID,
  ComponentType.LAYOUT,
  ComponentType.SMART_CONTENT,
];

/** True when every answerable component on the page has been answered (via local model or backend state) */
const allComponentsAnswered = computed(() => {
  return page.value.components.every((comp, i) =>
    NON_ANSWERABLE_TYPES.includes(comp.type) || isComponentAnswered(comp as Component, i));
});

/** Tracks whether isContentRead:true was already sent for this page to prevent duplicate PUTs */
const contentReadSubmitted = ref(false);

function assignCurrentIndex(): void {
  if (session.value?.state === SessionState.COMPLETED) {
    currentIndex.value = page.value.components.length - 1;
  } else {
    let number = page.value.components.findLastIndex(comp =>
      comp.questionnaire?.isAnswered ||
      comp.conceptualizationWorksheet?.isAnswered ||
      comp.problemSolvingWorksheet?.isAnswered ||
      comp.journal?.isAnswered);
    if (number < page.value.components.length - 1) {
      number++;
    }
    if (number === 0
      && page.value.components!.length > 0
      && page.value.components![0].type === ComponentType.CONTENT
      && (page.value.components![0] as Component).content?.displayType === ContentDisplayType.NO_TEXT) {
      number = 1;
    }
    currentIndex.value = number;
  }
}

async function submitAndSync(isContentRead: boolean): Promise<void> {
  const answers: SessionRequest[] = model.value
    .flatMap((val: SessionRequest[]) => val)
    .filter((el: SessionRequest | null) => el);
  const updated = await api.me.putSession(
    iid.value,
    sid.value,
    page.value.id,
    {
      answers,
      isContentRead,
    },
  );
  // Defensive: if the BE is rolled back to 204 No Content, the fetch wrapper
  // returns null. Avoid clobbering intervention.value (the session template
  // dereferences it non-null-asserted and would crash mid-therapy).
  if (updated) {
    intervention.value = updated;
    if (isContentRead) {
      contentReadSubmitted.value = true;
    }
  }
}

const [saveSession, saving] = useLoadingNotification(
  async(goNext): Promise<Intervention> => {
    const currentModel: SessionRequest[] | null = model.value[currentIndex.value] ?? null;
    // Compute the would-be state for PUT decisions/payload without mutating
    // currentIndex yet. If the PUT fails, useLoadingNotification's catch
    // surfaces the toast and we exit before advancing — leaving the UI in a
    // consistent state with the BE. The previous optimistic order
    // (increment + scroll → await) advanced the section visibly during the
    // PUT, then showed an error toast without rolling back (IPD-2759 review
    // miguelbcr). Pre-existing on develop, but the inline-saving change
    // exposed it.
    const prospectiveIndex = goNext ? currentIndex.value + 1 : currentIndex.value;
    const prospectiveAllContentRead = prospectiveIndex >= page.value.components.length;
    const isContentRead = prospectiveAllContentRead || allComponentsAnswered.value;
    const hasPendingPut = !contentReadSubmitted.value && (
      !goNext
      || ((currentModel?.length ?? 0) > 0) // if there are data to update
      || prospectiveAllContentRead /// if we need to mark current page as read
    );
    if (
      hasPendingPut &&
      (session.value?.state === SessionState.UNLOCK) && // if we session is not in progress we shouldn't even be here
      page.value.state === PageState.UNLOCK // if we are browsing previus page or future page, skip this block
    ) {
      await submitAndSync(isContentRead);
    }
    // PUT succeeded (or wasn't needed). Now safe to advance the visible state.
    if (goNext) {
      logger.info(`Increasing currentIndex ${currentIndex.value}`)();
      currentIndex.value++;
      if (currentIndex.value < page.value.components.length) {
        const revealedIndex = currentIndex.value;
        scrollToSection(() => sectionRefs.resolve(revealedIndex));
      }
    }
    // Navigate after store is up to date
    if (allContentRead.value && goNext) {
      navigating.value = true;
      // Always clear the flag on settle: the pid watcher resets it on
      // successful navigation, but a cancelled/redirected push would
      // leave it stuck true and the continue button disabled.
      void router.push(nextPageLink.value).finally(() => {
        navigating.value = false;
      });
    }
    return intervention.value!;
  },
);

watch(pid, async(newPid) => {
  logger.debug('pid')();
  model.value = [];
  sectionRefs.reset();
  // saving is intentionally omitted — it's managed by useLoadingNotification's
  // try/finally; resetting it externally would race with the wrapper.
  hideContinue.value = false;
  navigating.value = false;
  contentReadSubmitted.value = false;
  assignCurrentIndex();

  // Page-to-page transition: jump to the top of the scroll container (showing
  // the new page's title-header briefly), then smooth-scroll down to the first
  // content card. The two-step gives the user a perceivable motion cue that
  // they've moved to a new page — without the jump, the destination is often
  // already near the current scroll position and the smooth scroll has nothing
  // visible to animate.
  await nextTick();
  // Two fast page changes (A→B→C) interleave through the await — without this
  // guard the older run would resume after the newer one's prologue and queue
  // a competing smooth-scroll (end state consistent, animation chaotic).
  // IPD-2759 review carmelojcortes.
  if (pid.value !== newPid) {
    return;
  }
  const firstContentIndex = currentIndex.value;
  // Anchor for the instant jump-to-top: prefer the intended target, but fall
  // back to any rendered section so we still locate the scroll container if
  // currentIndex landed past the rendered range. Without this fallback, an
  // unexpected undefined target would silently skip both scrolls and leave the
  // user at their previous scroll position on the new page.
  const scrollAnchor = sectionRefs.resolve(firstContentIndex)
    ?? (sectionRefs.refs.value.find(r => r)?.$el as HTMLElement | undefined)
    ?? null;
  if (scrollAnchor) {
    const scrollContainer = findScrollContainer(scrollAnchor);
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }
  scrollToSection(() => sectionRefs.resolve(firstContentIndex));
});

onBeforeMount(() => {
  logger.debug('onBeforeMount')();
  assignCurrentIndex();
});


</script>
<template>
  <q-form
    class="qform"
    @submit="saveSession(true)"
    @validation-error="onValidationError"
  >
    <div class="form-inner">
      <template v-for="(component, i) in page.components" :key="(component as any).id || i">
        <section-component
          v-if="i <= currentIndex"
          :ref="el => sectionRefs.assign(el, i)"
          v-model="model[i]"
          v-model:hide-continue="hideContinue"
          :thinking-traps="intervention!.thinkingTraps"
          :primary-feelings="intervention!.primaryFeelings"
          :loading="saving"
          :iid="iid"
          :sid="sid"
          :component="component"
          @update="saveSession(false)"
        />
      </template>
      <balm-btn
        v-if="!hideContinue"
        class="continue-btn"
        :label="nextPageLink ? $t('continue') : $t('finish')"
        :disable="saving || navigating"
        :loading="saving || navigating"
        type="submit"
        theme="blue"
      />
    </div>
  </q-form>
</template>
<style scoped lang="scss">

.form-inner {
  margin: 0 auto;
  display: flex;
  width: 100%; // e.g. no-content block is too small w/o it
  flex-direction: column;
  gap: var(--Spacing-spacing-5);
}

.continue-btn {
  margin-left: auto;
}
</style>
